import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../../lib/analytics";
import { performLogout } from "../../lib/auth-logout";
import { useClientTranslations } from "../../lib/i18n/client";
import * as ProfileService from "../../services/profileService";
import styles from "./ProfileModal.module.css";

interface DeleteAccountModalProps {
	isOpen: boolean;
	onClose: () => void;
	onDeleteSuccess?: () => void;
}

export function DeleteAccountModal({
	isOpen,
	onClose,
	onDeleteSuccess,
}: DeleteAccountModalProps) {
	const { t, direction } = useClientTranslations("profile");
	const { logProfile } = useAnalytics();
	const { logout } = useAuth0();
	const [step, setStep] = useState<1 | 2>(1);
	const [isDeleting, setIsDeleting] = useState(false);

	if (!isOpen) return null;

	const handleClose = () => {
		setStep(1);
		onClose();
	};

	const handleContinue = () => {
		logProfile("delete_account_step_1_confirmed");
		setStep(2);
	};

	const handleConfirmDelete = async () => {
		logProfile("delete_account_step_2_confirmed");
		setIsDeleting(true);

		try {
			await ProfileService.deleteAccount();

			logProfile("delete_account_success");
			toast.success(t("deleteAccount.successMessage"));

			// Close modal and trigger callback
			handleClose();
			if (onDeleteSuccess) {
				onDeleteSuccess();
			}

			// Wait a moment for the success message to show, then logout
			setTimeout(async () => {
				console.log("🚪 Account deleted, logging out user...");
				await performLogout(logout);
			}, 2000);
		} catch (error) {
			console.error("Error requesting account deletion:", error);
			logProfile("delete_account_error");
			toast.error(t("deleteAccount.errorMessage"));
			setIsDeleting(false);
		}
	};

	const handleCancel = () => {
		logProfile(`delete_account_step_${step}_cancelled`);
		handleClose();
	};

	return (
		<div className={styles.overlay}>
			<div className={styles.modal} dir={direction}>
				{/* Modal Header */}
				<div className={styles.header}>
					<h2 className={styles.title}>
						{step === 1
							? t("deleteAccount.step1.title")
							: t("deleteAccount.step2.title")}
					</h2>
					<button
						type="button"
						onClick={handleCancel}
						className={styles.closeButton}
						aria-label="Close modal"
						disabled={isDeleting}
					>
						<svg
							className={styles.closeIcon}
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<title>Close</title>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M6 18L18 6M6 6l12 12"
							/>
						</svg>
					</button>
				</div>

				{/* Modal Content */}
				<div className={styles.content}>
					{step === 1 ? (
						<div>
							<p>{t("deleteAccount.step1.message")}</p>
							<p style={{ marginTop: "1rem", fontWeight: 500 }}>
								{t("deleteAccount.step1.warning")}
							</p>
						</div>
					) : (
						<div>
							<p>{t("deleteAccount.step2.message")}</p>
							<p style={{ marginTop: "1rem", fontWeight: 500 }}>
								{t("deleteAccount.step2.emailNotice")}
							</p>
						</div>
					)}
				</div>

				{/* Modal Actions */}
				<div className={styles.actions}>
					<div className={styles.buttonGroup}>
						<button
							type="button"
							onClick={handleCancel}
							className={`${styles.button} ${styles.cancelButton}`}
							disabled={isDeleting}
						>
							{t("common.cancel")}
						</button>
						{step === 1 ? (
							<button
								type="button"
								onClick={handleContinue}
								className={`${styles.button} ${styles.saveButton}`}
								style={{ backgroundColor: "#dc2626" }}
								disabled={isDeleting}
							>
								{t("deleteAccount.step1.continueButton")}
							</button>
						) : (
							<button
								type="button"
								onClick={handleConfirmDelete}
								className={`${styles.button} ${styles.saveButton}`}
								style={{ backgroundColor: "#dc2626" }}
								disabled={isDeleting}
							>
								{isDeleting
									? t("deleteAccount.step2.deletingButton")
									: t("deleteAccount.step2.confirmButton")}
							</button>
						)}
					</div>
				</div>
			</div>
		</div>
	);
}
